import React, { Component } from "react";
import { AxiosResponse } from "axios";
import { Link } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { getRequest } from "../../utils/httpRequest";
import { errorNotification } from "../../utils/helpers";
import { BlogPostCard } from "./types";
import BlogSubscribe from "./BlogSubscribe";

interface State {
  loading: boolean;
  loadingMore: boolean;
  searching: boolean;
  nextPage?: number;
  posts: BlogPostCard[];
  hasMore: boolean;
  query: string;
}

const SEARCH_DEBOUNCE_MS = 350;

interface BlogPostsResponse {
  posts: BlogPostCard[];
  pagination: {
    current_page: number;
    next_page?: number;
    total_pages: number;
    total_count: number;
  };
}

class BlogIndex extends Component<{}, State> {
  // Monotonic token so responses from superseded searches are ignored.
  private requestSeq = 0;
  private searchTimer?: number;

  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      loadingMore: false,
      searching: false,
      posts: [],
      hasMore: true,
      query: ""
    };
  }

  componentDidMount() {
    document.title = "Blog | 42courses";
    return this.fetchPosts();
  }

  componentWillUnmount() {
    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer);
    }
  }

  fetchPosts = (page = 1) => {
    if (page > 1 && this.state.loadingMore) {
      return null;
    }

    const seq = ++this.requestSeq;
    const query = this.state.query.trim();
    const queryParam = query ? `&q=${encodeURIComponent(query)}` : "";

    // `searching` keeps the hero + search box on screen during a refetch;
    // `loading` is only the very first page load.
    this.setState({ loadingMore: page > 1, searching: page === 1 });

    return getRequest(`/api/blog-posts.json?page=${page}${queryParam}`)
      .then((response: AxiosResponse<BlogPostsResponse>) => {
        // Drop the response if a newer request has since been issued.
        if (seq !== this.requestSeq) {
          return;
        }

        const { posts, pagination } = response.data;

        this.setState(prevState => ({
          posts: page === 1 ? posts : [...prevState.posts, ...posts],
          loading: false,
          loadingMore: false,
          searching: false,
          nextPage: pagination.next_page,
          hasMore: Boolean(pagination.next_page)
        }));
      })
      .catch(error => {
        if (seq !== this.requestSeq) {
          return;
        }

        this.setState({ loading: false, loadingMore: false, searching: false });
        errorNotification(
          "Error loading blog posts",
          JSON.stringify(error.response?.data || error.message)
        );
      });
  };

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;

    this.setState({ query });

    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer);
    }

    this.searchTimer = window.setTimeout(() => this.fetchPosts(1), SEARCH_DEBOUNCE_MS);
  };

  handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer);
    }

    this.fetchPosts(1);
  };

  fetchMorePosts = () => {
    const { nextPage, hasMore } = this.state;

    if (!hasMore || !nextPage) {
      return null;
    }

    return this.fetchPosts(nextPage);
  };

  render() {
    const { loading, searching, posts, query } = this.state;

    if (loading) {
      return (
        <main className="blog-page">
          <div className="ui active inverted dimmer">
            <div className="ui text loader">Loading</div>
          </div>
        </main>
      );
    }

    return (
      <div className="blog-page">
        <header className="blog-page__hero">
          <span className="blog-page__watermark" aria-hidden="true">42</span>
          <p className="blog-page__intro">
            Ideas, examples, and practical learning from the <em>42courses</em> team and our expert teachers.
          </p>

          <form
            className="blog-page__search"
            role="search"
            onSubmit={this.handleSearchSubmit}
          >
            <i className="search icon" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={this.handleSearchChange}
              placeholder="Search posts by title or content…"
              aria-label="Search blog posts"
            />
          </form>

          <BlogSubscribe variant="bar" />
        </header>

        <main className="blog-page__main">
          {posts.length === 0 ? (
            <div className="blog-page__empty">
              {searching ? (
                <div className="ui active centered inline loader blog-page__loader" />
              ) : (
                <p>
                  No posts found
                  {query.trim() ? <> for “{query.trim()}”</> : null}.
                </p>
              )}
            </div>
          ) : (
            <InfiniteScroll
              className="blog-page__scroll"
              dataLength={posts.length}
              hasMore={this.state.hasMore}
              loader={<div className="ui active centered inline loader blog-page__loader" />}
              next={this.fetchMorePosts}
            >
              <div className="blog-page__grid">
                {posts.map(post => (
                  <article className="blog-page__card" key={post.id}>
                    <Link to={post.blog_post_path}>
                      {post.hero_image_url && (
                        <div className="blog-page__thumb">
                          <img src={post.hero_image_url} alt="" loading="lazy" />
                        </div>
                      )}
                      <div className="blog-page__card-body">
                        {post.skill_area && <em>{post.skill_area.name}</em>}
                        <span>{new Date(post.published_at).toDateString()}</span>
                        <h2>{post.title}</h2>
                        <p>{post.excerpt}</p>
                        <strong>Read post</strong>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </InfiniteScroll>
          )}
        </main>
      </div>
    );
  }
}

export default BlogIndex;
